import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Shield, TrendingUp, Copy, Check, CheckCircle,
  Lock, Unlock, ArrowUp, Eye, Zap, Settings,
  AlertTriangle, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsTrustLadder() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const tiers = [
    {
      name: 'Probation',
      color: '#EF4444',
      icon: <Lock className="w-5 h-5" />,
      subtitle: 'All actions need approval',
      description: 'Every agent starts at Probation. All decisions — regardless of value or confidence — require explicit merchant approval. This lets you observe the agent\'s judgment before granting any autonomy. The agent proposes actions and waits for you to approve or reject each one.',
      decisions_required: '0 (starting tier)',
      success_rate: 'N/A',
      min_days: 'N/A',
      auto_approve_below: '$0',
      force_approval_above: '$0',
    },
    {
      name: 'Supervised',
      color: '#F59E0B',
      icon: <Eye className="w-5 h-5" />,
      subtitle: 'Low-value auto-approved up to $50',
      description: 'The agent has demonstrated consistent judgment on initial decisions. Low-value actions (under $50) are auto-approved — things like small discount adjustments, inventory reorder suggestions, and routine follow-up messages. High-value actions still require your approval.',
      decisions_required: '20+ decisions at 80%+ success rate',
      success_rate: '80%',
      min_days: '3+ days',
      auto_approve_below: '$50',
      force_approval_above: '$200',
    },
    {
      name: 'Trusted',
      color: '#3B82F6',
      icon: <CheckCircle className="w-5 h-5" />,
      subtitle: 'Most auto-approved up to $500',
      description: 'The agent has built a strong track record over two weeks. Most actions up to $500 are auto-approved — discount offers, deal adjustments, inventory orders, customer outreach. Only exceptions and high-value decisions above $500 require manual approval. This is where most well-performing agents settle.',
      decisions_required: '100+ decisions at 90%+ success rate',
      success_rate: '90%',
      min_days: '14+ days',
      auto_approve_below: '$500',
      force_approval_above: '$2,000',
    },
    {
      name: 'Autonomous',
      color: '#3EF1C7',
      icon: <Zap className="w-5 h-5" />,
      subtitle: 'Full autonomy up to $5,000 within guardrails',
      description: 'Elite tier for agents with exceptional track records. The agent operates with full autonomy for actions up to $5,000 — only extraordinary situations require approval. Guardrails still apply (max discount percentages, forbidden actions, escalation rules). This tier is earned, not granted.',
      decisions_required: '500+ decisions at 95%+ success rate',
      success_rate: '95%',
      min_days: '30+ days',
      auto_approve_below: '$5,000',
      force_approval_above: '$10,000',
    },
  ];

  const approvalOverrides = [
    { tier: 'Probation', autoBelow: '$0', forceAbove: '$0', description: 'All actions require approval' },
    { tier: 'Supervised', autoBelow: '$50', forceAbove: '$200', description: 'Small actions auto-approved' },
    { tier: 'Trusted', autoBelow: '$500', forceAbove: '$2,000', description: 'Most actions auto-approved' },
    { tier: 'Autonomous', autoBelow: '$5,000', forceAbove: '$10,000', description: 'Full autonomy within guardrails' },
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Trust Ladder
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Trust Ladder
        </h1>
        <p className="text-lg text-[#94A3B8]">
          The progressive autonomy system that governs how much freedom AI agents earn based
          on their track record. Agents start restricted and earn more autonomy through
          consistent, high-quality decisions.
        </p>
      </div>

      {/* Why Trust Ladder */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Why the Trust Ladder?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <p className="text-[#94A3B8] mb-4">
              Giving an AI agent full autonomy on day one is risky. Requiring manual approval for every
              action forever is unsustainable. The Trust Ladder solves both problems with
              <span className="text-white font-medium"> progressive autonomy</span> — agents earn
              freedom through performance.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Safety first</span>
                  <p className="text-sm text-[#94A3B8]">New agents prove themselves before getting autonomy. Bad judgment is caught early while stakes are low.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Scalable operations</span>
                  <p className="text-sm text-[#94A3B8]">As agents earn trust, they handle more decisions autonomously. Your approval queue shrinks over time.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Merchant control</span>
                  <p className="text-sm text-[#94A3B8]">You can override tiers at any time. Set minimum tiers, cap maximum tiers, or disable auto-upgrade entirely.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* The 4 Tiers */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          The 4 Tiers
        </h2>
        <div className="space-y-6">
          {tiers.map((tier, i) => (
            <Card key={i} className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center border-2"
                    style={{ borderColor: tier.color, backgroundColor: `${tier.color}15`, color: tier.color }}
                  >
                    {tier.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: tier.color }}>{tier.name}</h3>
                    <p className="text-[#64748B] text-sm">{tier.subtitle}</p>
                  </div>
                  <Badge className="ml-auto text-xs" style={{
                    backgroundColor: `${tier.color}20`,
                    color: tier.color,
                    borderColor: `${tier.color}30`
                  }}>
                    Tier {i + 1}
                  </Badge>
                </div>

                <p className="text-[#94A3B8] text-sm mb-4">{tier.description}</p>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
                  <div>
                    <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Requirements</p>
                    <p className="text-[#E2E8F0] text-sm font-medium">{tier.decisions_required}</p>
                  </div>
                  <div>
                    <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Success Rate</p>
                    <p className="text-[#E2E8F0] text-sm font-medium">{tier.success_rate}</p>
                  </div>
                  <div>
                    <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Min Age</p>
                    <p className="text-[#E2E8F0] text-sm font-medium">{tier.min_days}</p>
                  </div>
                  <div>
                    <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Auto-Approve Below</p>
                    <p className="text-sm font-medium" style={{ color: tier.color }}>{tier.auto_approve_below}</p>
                  </div>
                  <div>
                    <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Force Approval Above</p>
                    <p className="text-rose-400 text-sm font-medium">{tier.force_approval_above}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How Upgrades Work */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          How Upgrades Work
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <p className="text-[#94A3B8] mb-4">
              Tier upgrades are evaluated automatically every time an agent makes a decision (on every
              agent listing). The system checks three criteria against the next tier's requirements:
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
                <BarChart3 className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Decision Count</p>
                  <p className="text-[#64748B] text-xs">Total number of decisions the agent has made since activation. Includes both auto-approved and manually reviewed decisions.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
                <TrendingUp className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Success Rate</p>
                  <p className="text-[#64748B] text-xs">Percentage of decisions that were successful — approved by merchant or achieved desired outcome. Rejected decisions count against the rate.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
                <ArrowUp className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Age Since First Decision</p>
                  <p className="text-[#64748B] text-xs">Number of days since the agent made its first decision. Prevents rapid promotion — even a perfect agent must prove consistency over time.</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <button
                onClick={() => copyToClipboard(`// Tier upgrade evaluation (runs on every agent decision)
function evaluateTierUpgrade(agent) {
  const stats = agent.getPerformanceStats();
  const nextTier = getNextTier(agent.current_tier);

  if (!nextTier) return; // Already at Autonomous

  const req = nextTier.requirements;
  const daysSinceFirst = daysBetween(stats.first_decision_at, now());

  if (
    stats.total_decisions >= req.min_decisions &&
    stats.success_rate >= req.min_success_rate &&
    daysSinceFirst >= req.min_days
  ) {
    agent.upgradeTo(nextTier.name);
    notifyMerchant(
      \`Agent "\${agent.name}" promoted to \${nextTier.name}!\n\` +
      \`Decisions: \${stats.total_decisions} | \` +
      \`Success: \${(stats.success_rate * 100).toFixed(1)}% | \` +
      \`Days active: \${daysSinceFirst}\`
    );
  }
}`, 'upgrade-logic')}
                className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
              >
                {copiedCode === 'upgrade-logic' ? (
                  <Check className="w-4 h-4 text-[#3EF1C7]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#64748B]" />
                )}
              </button>
              <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`// Tier upgrade evaluation (runs on every agent decision)
function evaluateTierUpgrade(agent) {
  const stats = agent.getPerformanceStats();
  const nextTier = getNextTier(agent.current_tier);

  if (!nextTier) return; // Already at Autonomous

  const req = nextTier.requirements;
  const daysSinceFirst = daysBetween(stats.first_decision_at, now());

  if (
    stats.total_decisions >= req.min_decisions &&
    stats.success_rate >= req.min_success_rate &&
    daysSinceFirst >= req.min_days
  ) {
    agent.upgradeTo(nextTier.name);
    notifyMerchant(
      \`Agent "\${agent.name}" promoted to \${nextTier.name}!\n\` +
      \`Decisions: \${stats.total_decisions} | \` +
      \`Success: \${(stats.success_rate * 100).toFixed(1)}% | \` +
      \`Days active: \${daysSinceFirst}\`
    );
  }
}`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Approval Overrides by Tier */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Approval Overrides by Tier
        </h2>
        <p className="text-[#94A3B8] mb-6">
          Each tier defines two dollar thresholds that control automatic approval behavior.
          Actions between the two thresholds follow the configured approval mode (auto, manual, or hybrid).
        </p>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#22304A]">
                    <th className="text-left p-4 text-[#94A3B8] font-medium">Tier</th>
                    <th className="text-left p-4 text-[#94A3B8] font-medium">auto_approve_below</th>
                    <th className="text-left p-4 text-[#94A3B8] font-medium">force_approval_above</th>
                    <th className="text-left p-4 text-[#94A3B8] font-medium">Behavior</th>
                  </tr>
                </thead>
                <tbody>
                  {approvalOverrides.map((row, i) => (
                    <tr key={i} className="border-b border-[#22304A] last:border-0">
                      <td className="p-4">
                        <span className="font-medium" style={{ color: tiers[i].color }}>{row.tier}</span>
                      </td>
                      <td className="p-4 text-[#3EF1C7] font-mono text-xs">{row.autoBelow}</td>
                      <td className="p-4 text-rose-400 font-mono text-xs">{row.forceAbove}</td>
                      <td className="p-4 text-[#94A3B8] text-xs">{row.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
          <p className="text-sm text-[#64748B]">
            <strong className="text-white">How the thresholds interact:</strong> Actions below
            <code className="text-[#3EF1C7] mx-1">auto_approve_below</code> are always auto-approved.
            Actions above <code className="text-rose-400 mx-1">force_approval_above</code> always require
            manual approval. Actions in between follow the agent's configured approval mode.
          </p>
        </div>
      </section>

      {/* Manual Override */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Manual Override
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <p className="text-[#94A3B8] mb-4">
              Merchants can override the automatic tier system at any time using the
              <code className="text-[#3EF1C7] mx-1">/mintier</code> command or the API.
              This sets a minimum tier floor — the agent will never drop below this tier,
              even if performance metrics temporarily dip. You can also use it to fast-track
              a trusted agent.
            </p>

            <div className="relative mb-4">
              <button
                onClick={() => copyToClipboard(`# Set minimum tier via messaging command
/mintier supervised

# Response:
# Minimum trust tier set to: Supervised
# All agents at Probation tier have been upgraded.
# Affected agents: 1 (Outreach Agent)

# Set for a specific agent via API
PUT /api/v1/agents/{agent_id}/trust-tier
{
  "min_tier": "trusted",
  "auto_upgrade_enabled": true
}

# Response:
{
  "agent_id": "agent_pricing_abc",
  "previous_tier": "supervised",
  "current_tier": "trusted",
  "min_tier": "trusted",
  "auto_upgrade_enabled": true,
  "next_tier_requirements": {
    "tier": "autonomous",
    "decisions_needed": 358,
    "success_rate_needed": "95%",
    "days_remaining": 16
  }
}`, 'manual-override')}
                className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
              >
                {copiedCode === 'manual-override' ? (
                  <Check className="w-4 h-4 text-[#3EF1C7]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#64748B]" />
                )}
              </button>
              <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`# Set minimum tier via messaging command
/mintier supervised

# Response:
# Minimum trust tier set to: Supervised
# All agents at Probation tier have been upgraded.
# Affected agents: 1 (Outreach Agent)

# Set for a specific agent via API
PUT /api/v1/agents/{agent_id}/trust-tier
{
  "min_tier": "trusted",
  "auto_upgrade_enabled": true
}

# Response:
{
  "agent_id": "agent_pricing_abc",
  "previous_tier": "supervised",
  "current_tier": "trusted",
  "min_tier": "trusted",
  "auto_upgrade_enabled": true,
  "next_tier_requirements": {
    "tier": "autonomous",
    "decisions_needed": 358,
    "success_rate_needed": "95%",
    "days_remaining": 16
  }
}`}
              </pre>
            </div>

            <div className="bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#64748B]">
                  <strong className="text-white">Use with caution:</strong> Setting
                  <code className="text-[#3EF1C7] mx-1">/mintier autonomous</code> gives all agents
                  full autonomy up to $5,000 regardless of their track record. This bypasses the
                  safety of progressive trust building. Consider setting it per-agent via the API
                  for more granular control.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Next Steps */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          Next Steps
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/commands">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Messaging Commands →</h3>
                <p className="text-sm text-[#94A3B8]">Use /agents and /mintier to manage tiers</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/approval-queue">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Approval Queue →</h3>
                <p className="text-sm text-[#94A3B8]">Review and manage pending agent decisions</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
